import { useState, useEffect, useCallback } from 'react';
import type {
  ReservationSlotTemplate,
  ReservationSlotTemplateInput,
} from '../../../../domain/reservationSlots';
import type { ReservationSlotTemplateRepository } from '../../../../application/reservationSlots/ports/ReservationSlotTemplateRepository';
import {
  getTemplates,
  saveTemplate,
  deleteTemplate,
} from '../../../../application/reservationSlots';

/**
 * useTemplateManagement フックの戻り値型
 */
export interface UseTemplateManagementReturn {
  templates: ReservationSlotTemplate[];
  isLoading: boolean;
  saving: boolean;
  error: string | null;
  dialogOpen: boolean;
  editingTemplate: ReservationSlotTemplate | null;
  deleteDialogOpen: boolean;
  deletingTemplate: ReservationSlotTemplate | null;
  reload: () => Promise<void>;
  openCreateDialog: () => void;
  openEditDialog: (template: ReservationSlotTemplate) => void;
  closeDialog: () => void;
  save: (input: ReservationSlotTemplateInput) => Promise<boolean>;
  openDeleteDialog: (template: ReservationSlotTemplate) => void;
  closeDeleteDialog: () => void;
  confirmDelete: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * テンプレート管理タブ用カスタムフック
 *
 * @description
 * 解放枠テンプレートの一覧取得・作成・更新・削除を統合管理する。
 * templates は基本設定・解放枠生成タブでも参照されるため、
 * 本フックをテンプレートの単一の情報源として利用する。
 *
 * @param {ReservationSlotTemplateRepository} repository - DIされるRepository実装
 */
export const useTemplateManagement = (
  repository: ReservationSlotTemplateRepository
): UseTemplateManagementReturn => {
  const [templates, setTemplates] = useState<ReservationSlotTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ReservationSlotTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<ReservationSlotTemplate | null>(null);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getTemplates(repository);
      setTemplates(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'テンプレート一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = useCallback(() => {
    setEditingTemplate(null);
    setDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((template: ReservationSlotTemplate) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingTemplate(null);
  }, []);

  const save = useCallback(
    async (input: ReservationSlotTemplateInput): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        await saveTemplate(repository, input, editingTemplate?.id);
        await reload();
        setDialogOpen(false);
        setEditingTemplate(null);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'テンプレートの保存に失敗しました');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [repository, editingTemplate, reload]
  );

  const openDeleteDialog = useCallback((template: ReservationSlotTemplate) => {
    setDeletingTemplate(template);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeletingTemplate(null);
  }, []);

  const confirmDelete = useCallback(async (): Promise<boolean> => {
    if (!deletingTemplate) {
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      await deleteTemplate(repository, deletingTemplate.id);
      await reload();
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'テンプレートの削除に失敗しました');
      return false;
    } finally {
      setSaving(false);
    }
  }, [repository, deletingTemplate, reload]);

  const clearError = useCallback(() => setError(null), []);

  return {
    templates,
    isLoading,
    saving,
    error,
    dialogOpen,
    editingTemplate,
    deleteDialogOpen,
    deletingTemplate,
    reload,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    save,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
    clearError,
  };
};
